import type { Timesheets } from './types/timesheet';
import UserError from '@/lib/request/errors/userError';
import { isDev } from '../util/isDev';
import { getTimesheetsMock } from './mock/timesheets.mock';
import { KioskCommand } from './postKioskApi';
import { usePostKioskApi } from './usePostKioskApi';
import axios from 'axios';
import { isErrorResponse } from '@/lib/request/isErrorResponse';

let devShiftState: string;

export async function postTimesheet(
  param: string,
  payload: any,
  authorization: string
): Promise<Timesheets> {
  try {
    const response = await axios.post(
      `/api/v1/my/timesheet/${param}`,
      payload,
      {
        headers: {
          Authorization: authorization,
        },
      }
    );
    return response?.data;
  } catch (e) {
    if (isErrorResponse(e) && e.response.status < 500) {
      throw new UserError(e.response.data.error, e.response.status);
    }

    throw e;
  }
}
export interface StartShiftRequestPayload {
  arrCustomFieldData: Record<string, string>;
  strEmployeeComment: string;
}

export async function startShift(
  opunitId: number,
  authorization: string,
  payload?: Partial<StartShiftRequestPayload>
) {
  if (isDev()) {
    devShiftState = 'onShift';
    return [getTimesheetsMock()[0]];
  }

  await postTimesheet(
    'start',
    {
      intOpunitId: opunitId,
      ...payload,
    },
    authorization
  );
}

export interface EndShiftRequestPayload {
  arrCustomFieldData: Record<string, string>;
  strEmployeeComment: string;
  strStartTime: string;
  strEndTime: string;
}

export async function endShift(
  timesheetId: number,
  authorization: string,
  payload?: Partial<EndShiftRequestPayload>
) {
  if (isDev()) {
    devShiftState = 'notOnShift';
    return [getTimesheetsMock()[3]];
  }

  await postTimesheet(
    'end',
    {
      intTimesheetId: timesheetId,
      ...payload,
    },
    authorization
  );
}

export async function pauseShift(
  timesheetId: number,
  authorization: string,
  breakType?: string,
  breakId?: number
) {
  if (isDev()) {
    devShiftState = breakType ? 'onBreak' : 'onShift';
    return breakType ? [getTimesheetsMock()[1]] : [getTimesheetsMock()[2]];
  }

  await postTimesheet(
    'pause',
    {
      intTimesheetId: timesheetId,
      strBreakAction: breakType,
      intBreakId: breakId,
    },
    authorization
  );
}

export async function fetchTimesheets(): Promise<Timesheets> {
  if (isDev()) {
    if (devShiftState == 'onShift') {
      return [getTimesheetsMock()[0]];
    } else if (devShiftState == 'onBreak') {
      return [getTimesheetsMock()[1]];
    } else {
      return [getTimesheetsMock()[3]];
    }
  }

  const postKioskApi = usePostKioskApi();
  return await postKioskApi<Timesheets>(KioskCommand.pullTimesheetFromServer);
}

export async function fetchUserTimesheets(
  authorization: string
): Promise<Timesheets> {
  if (isDev()) {
    if (devShiftState == 'onShift') {
      return [getTimesheetsMock()[0]];
    } else if (devShiftState == 'onBreak') {
      return [getTimesheetsMock()[1]];
    } else {
      return [getTimesheetsMock()[3]];
    }
  }

  const response = await axios.get('/api/v1/my/timesheet', {
    headers: {
      Authorization: authorization,
    },
  });

  return response?.data;
}
